const ZONA_DEFAULT = 'America/Argentina/Buenos_Aires';
const FORMATO_FECHA_DEFAULT = 'DD_MM_YYYY';
const FORMATOS_FECHA = ['DD_MM_YYYY', 'MM_DD_YYYY'];

const ZONAS_DISPONIBLES = [
  'America/Argentina/Buenos_Aires',
  'America/Argentina/Cordoba',
  'America/Argentina/Mendoza',
  'America/Argentina/Salta',
  'America/Argentina/Tucuman',
  'America/Argentina/Ushuaia',
  'America/Montevideo',
  'America/Santiago',
  'America/Asuncion',
  'America/Sao_Paulo',
  'America/Bogota',
  'America/Lima',
  'America/Mexico_City',
  'America/Caracas',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Europe/Madrid',
  'UTC',
];

let zonaActiva = ZONA_DEFAULT;
let formatoFechaActivo = FORMATO_FECHA_DEFAULT;

export function setZonaHorariaSistema(zona) {
  zonaActiva = zona || ZONA_DEFAULT;
}

export function getZonaHorariaSistema() {
  return zonaActiva;
}

export function setFormatoFechaSistema(formato) {
  formatoFechaActivo = normalizarFormatoFecha(formato);
}

export function getFormatoFechaSistema() {
  return formatoFechaActivo;
}

export function getFormatosFechaDisponibles() {
  return FORMATOS_FECHA;
}

export function getZonasDisponibles() {
  return ZONAS_DISPONIBLES;
}

export function normalizarZona(zona) {
  if (zona && ZONAS_DISPONIBLES.includes(zona)) return zona;
  if (zona) {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: zona });
      return zona;
    } catch {
      if (typeof zona === 'string' && zona.includes('/')) return zona;
    }
  }
  return ZONA_DEFAULT;
}

export function normalizarFormatoFecha(formato) {
  const f = String(formato || '').toUpperCase().replace(/-/g, '_');
  if (FORMATOS_FECHA.includes(f)) return f;
  return FORMATO_FECHA_DEFAULT;
}

export function etiquetaFormatoFecha(formato) {
  const f = normalizarFormatoFecha(formato);
  return f === 'MM_DD_YYYY' ? 'mm/dd/aaaa' : 'dd/mm/aaaa';
}

function partesEnZona(date, timeZone) {
  const tz = normalizarZona(timeZone);
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(date instanceof Date ? date : new Date(date));
    const get = (type) => parts.find((p) => p.type === type)?.value;
    return {
      year: get('year'),
      month: get('month'),
      day: get('day'),
      hour: get('hour'),
      minute: get('minute'),
      second: get('second'),
    };
  } catch {
    const d = date instanceof Date ? date : new Date(date);
    return {
      year: String(d.getFullYear()),
      month: String(d.getMonth() + 1).padStart(2, '0'),
      day: String(d.getDate()).padStart(2, '0'),
      hour: String(d.getHours()).padStart(2, '0'),
      minute: String(d.getMinutes()).padStart(2, '0'),
      second: String(d.getSeconds()).padStart(2, '0'),
    };
  }
}

function armarFechaDisplay(day, month, year, formato) {
  const fmt = normalizarFormatoFecha(formato);
  if (fmt === 'MM_DD_YYYY') return `${month}/${day}/${year}`;
  return `${day}/${month}/${year}`;
}

function parseDateForDisplay(f) {
  if (!f) return null;
  if (typeof f === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(f)) {
    return new Date(`${f}T12:00:00`);
  }
  const d = new Date(f);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

export function fechaHoySistema(zona = zonaActiva) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: normalizarZona(zona),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function formatFechaSistema(f, opts = {}) {
  const zona = normalizarZona(opts.zona || zonaActiva);
  const formato = normalizarFormatoFecha(opts.formato ?? formatoFechaActivo);
  const vacio = opts.vacio ?? '-';
  if (!f) return vacio;
  const d = parseDateForDisplay(f);
  if (!d) return String(f);
  const p = partesEnZona(d, zona);
  return armarFechaDisplay(p.day, p.month, p.year, formato);
}

export function formatFechaHoraSistema(f, zona = zonaActiva, formato = formatoFechaActivo) {
  if (!f) return '-';
  const d = parseDateForDisplay(f);
  if (!d) return String(f);
  const p = partesEnZona(d, zona);
  const fecha = armarFechaDisplay(p.day, p.month, p.year, formato);
  return `${fecha}, ${p.hour}:${p.minute}`;
}

/**
 * Parsea una fecha mostrada (dd/mm/aaaa o mm/dd/aaaa) a ISO YYYY-MM-DD.
 * También acepta ISO ya válido.
 */
export function parseFechaDisplayToISO(str, formato = formatoFechaActivo) {
  if (!str) return null;
  const s = String(str).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (!m) return null;
  const a = parseInt(m[1], 10);
  const b = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  const fmt = normalizarFormatoFecha(formato);
  let day;
  let month;
  if (fmt === 'MM_DD_YYYY') {
    month = a;
    day = b;
  } else {
    day = a;
    month = b;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function relojSistema(zona = zonaActiva) {
  return new Intl.DateTimeFormat('es-AR', {
    timeZone: normalizarZona(zona),
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date());
}

export function etiquetaZona(zona) {
  return (zona || ZONA_DEFAULT).replace(/_/g, ' ');
}

export { FORMATO_FECHA_DEFAULT, FORMATOS_FECHA, ZONA_DEFAULT };
